import { type ContractTemplateRow } from "./TemplateModal";

/* ─── Language label ─────────────────────────────────────────── */
const LANG_LABELS: Record<string, string> = {
  en: "English", es: "Español", pt: "Português",
  fr: "Français", de: "Deutsch", it: "Italiano",
};

interface Props {
  template: ContractTemplateRow | null;
  onClose:  () => void;
  onEdit:   (t: ContractTemplateRow) => void;
}

export default function PreviewModal({ template, onClose, onEdit }: Props) {
  if (!template) return null;

  /* Highlight {{variables}} */
  const highlighted = template.content
    .split(/(\{\{[^}]+\}\})/g)
    .map((part, i) =>
      /^\{\{[^}]+\}\}$/.test(part)
        ? (
          <mark
            key={i}
            className="bg-[#C8A96E]/15 text-[#C8A96E] rounded px-0.5 not-italic font-medium"
          >
            {part}
          </mark>
        )
        : <span key={i}>{part}</span>,
    );

  const lineCount = template.content.split("\n").length;
  const charCount = template.content.length;
  const varCount  = [...new Set(template.content.match(/\{\{[^}]+\}\}/g) ?? [])].length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-3xl bg-white rounded-2xl border border-slate-200 shadow-xl flex flex-col" style={{ maxHeight: "90vh" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 flex-shrink-0">
              <i className="ri-file-text-line text-[#C8A96E]"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-[#0f172a] font-semibold text-sm truncate">{template.name}</h2>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-slate-400 text-[10px]">{LANG_LABELS[template.language] ?? template.language}</span>
                <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                <span className="text-slate-400 text-[10px]">{lineCount} lines · {charCount.toLocaleString()} chars</span>
                {varCount > 0 && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                    <span className="text-[#C8A96E] text-[10px]">{varCount} variable{varCount !== 1 ? "s" : ""}</span>
                  </>
                )}
                <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                <span className={`text-[10px] font-medium ${template.is_active ? "text-emerald-600" : "text-slate-400"}`}>
                  {template.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => { onClose(); onEdit(template); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-2-line text-sm"></i>
              Edit
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 transition-all cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        {/* Variable legend */}
        <div className="px-6 py-2.5 border-b border-slate-100 bg-[#C8A96E]/3 flex-shrink-0">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <i className="ri-information-line flex-shrink-0"></i>
            <span>
              <mark className="bg-[#C8A96E]/15 text-[#C8A96E] rounded px-0.5 not-italic text-[10px]">{"{{highlighted}}"}</mark>
              <span className="ml-1.5">variables will be replaced with real values when generating a contract.</span>
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <pre className="text-[#0f172a]/80 text-sm leading-relaxed whitespace-pre-wrap font-sans break-words">
            {highlighted}
          </pre>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 flex-shrink-0">
          <p className="text-slate-400 text-xs">
            Updated {new Date(template.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}